import React, { useState, useEffect, useCallback } from "react";
import { PageSection, Surface, relativeTime } from "@/components/shared";
import { formatDateTime } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Loader2, Database, Download, PlayCircle, RefreshCw, CheckCircle2, AlertCircle, HardDrive } from "lucide-react";
import api, { getToken } from "@/lib/api";
import { toast } from "sonner";

const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(b >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};

const fmtDate = (iso) => formatDateTime(iso) || "—";

export default function Backups() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [retention, setRetention] = useState(3);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hist, st] = await Promise.all([
        api.get("/backup/history?limit=50"),
        api.get("/backup/status"),
      ]);
      setRows(hist.data.rows || []);
      setRetention(hist.data.retention_count || 3);
      setStatus(st.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load backups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while a backup is running
  useEffect(() => {
    const has_running = rows.some((r) => r.status === "running") || status?.running;
    if (!has_running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [rows, status, load]);

  const runBackup = async () => {
    setTriggering(true);
    try {
      await api.post("/backup/run");
      toast.success("Backup started — mongodump + Bunny upload in progress.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to trigger backup");
    } finally {
      setTriggering(false);
    }
  };

  const downloadBackup = (row) => {
    const token = getToken();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/backup/${row.id}/download?auth=${encodeURIComponent(token)}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = row.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Downloading ${row.filename}…`);
  };

  const verdictColor = {
    healthy: "text-emerald-700 bg-emerald-50 border-emerald-200",
    stale:   "text-amber-700 bg-amber-50 border-amber-200",
    never:   "text-red-700 bg-red-50 border-red-200",
    unknown: "text-gray-600 bg-gray-50 border-gray-200",
  }[status?.verdict || "unknown"];

  return (
    <div className="space-y-6" data-testid="backups-page">
      <PageSection
        title="Database Backups"
        description={`Full DB backups uploaded to Bunny CDN daily at 02:00 IST. Only the last ${retention} successful backups are retained.`}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-9" data-testid="backups-reload">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1.5">Reload</span>
            </Button>
            <Button
              onClick={runBackup}
              disabled={triggering || !!status?.running}
              className="h-9 bg-[#9B1C1C] hover:bg-[#7F1D1D]"
              data-testid="backups-run-now"
            >
              {triggering || status?.running
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
                : <><PlayCircle className="h-4 w-4 mr-2" /> Backup now</>}
            </Button>
          </div>
        }
      >
        {/* Health card */}
        <div className={`rounded-md border px-5 py-4 ${verdictColor} mb-4`} data-testid="backups-health">
          <div className="flex items-start gap-3">
            {status?.verdict === "healthy" ? <CheckCircle2 className="h-6 w-6 shrink-0 mt-0.5" />
              : <AlertCircle className="h-6 w-6 shrink-0 mt-0.5" />}
            <div className="flex-1">
              <p className="font-heading font-semibold">
                {status?.verdict === "healthy" && "Backups are up-to-date"}
                {status?.verdict === "stale"   && "Last backup is more than 27h old"}
                {status?.verdict === "never"   && "No successful backup on record"}
                {status?.verdict === "unknown" && "Backup status unavailable"}
              </p>
              <p className="text-xs mt-1 opacity-80">
                {status?.latest ? <>
                  Last successful backup <b>{relativeTime(status.latest.completed_at)}</b> — <span className="font-mono">{status.latest.filename}</span> · {fmtBytes(status.latest.size_bytes)}
                </> : "Trigger the first backup with the “Backup now” button above."}
              </p>
            </div>
          </div>
        </div>

        <Surface padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-4 py-3">File</th>
                  <th className="px-4 py-3 w-32">Size</th>
                  <th className="px-4 py-3 w-40">Started</th>
                  <th className="px-4 py-3 w-24">Duration</th>
                  <th className="px-4 py-3 w-28">Trigger</th>
                  <th className="px-4 py-3 w-28">Status</th>
                  <th className="px-4 py-3 w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>)}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">No backups yet. Click <b>Backup now</b> to create the first one.</td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50" data-testid={`backup-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <HardDrive className="h-4 w-4 text-gray-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-mono text-xs text-gray-900 truncate">{r.filename}</p>
                          <p className="text-[10px] text-gray-500 truncate">{(r.collections_included || []).length} collections</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-mono text-xs">{fmtBytes(r.size_bytes)}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">{fmtDate(r.started_at)}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">{r.duration_seconds ?? "—"}s</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        r.triggered_by === "auto" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"
                      }`}>{r.triggered_by}</span>
                    </td>
                    <td className="px-4 py-3">
                      {r.status === "success" && <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold"><CheckCircle2 className="h-3 w-3" /> Success</span>}
                      {r.status === "running" && <span className="inline-flex items-center gap-1 text-xs text-blue-700 font-semibold"><Loader2 className="h-3 w-3 animate-spin" /> Running</span>}
                      {r.status === "failed" && (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-1 text-xs text-red-700 font-semibold"><AlertCircle className="h-3 w-3" /> Failed</span>
                          {r.error && <span className="text-[10px] text-red-700 leading-snug break-all">{r.error.slice(0, 200)}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === "success" && (
                        <Button
                          variant="outline" size="sm"
                          onClick={() => downloadBackup(r)}
                          className="h-8"
                          data-testid={`backup-download-${r.id}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                          <span className="ml-1.5 text-xs">Download</span>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Surface>
        <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
          <Database className="inline h-3 w-3 mr-1 -mt-0.5" />
          Backups store the full MongoDB dump as a gzipped tarball on Bunny CDN under <span className="font-mono">/backups/</span>.
          Automatic daily run: 02:00 IST. Retention: last {retention} successful backups.
        </p>
      </PageSection>
    </div>
  );
}
